import { defineStore } from 'pinia'
import { type Ref, computed, onScopeDispose, ref } from 'vue'

import { ADD_PAGE_REFUSAL_MESSAGES } from '@/components/strategy/AddPageDialog/add-page'
import {
  PLAN_GATE_REFUSAL_MESSAGES,
  START_PLAN_REFUSAL_MESSAGES,
  TOPIC_MISSING_MESSAGE,
} from '@/components/strategy/PlanRunNotice/plan-run-notice'
import { i18n } from '@/i18n'
import { type CallableError, createReasonGuard, parseCallableError } from '@/lib/firebase/errors'
import { createRetainer } from '@/lib/live/retainer'
import {
  addPage as repoAddPage,
  answerPlanGate as repoAnswerPlanGate,
  cancelPlanRun as repoCancelPlanRun,
  discardPlanProposal as repoDiscardPlanProposal,
  editPlanStep as repoEditPlanStep,
  rerunPlanFrom as repoRerunPlanFrom,
  startPlanRun as repoStartPlanRun,
  subscribeInventoryPages as repoSubscribeInventoryPages,
  subscribeKeywordRanks as repoSubscribeKeywordRanks,
  subscribeLatestCompletedPlanRun as repoSubscribeLatestCompletedPlanRun,
  subscribeLatestPlanRun as repoSubscribeLatestPlanRun,
  subscribePageKeywords as repoSubscribePageKeywords,
  subscribePages as repoSubscribePages,
  subscribePlanEdits as repoSubscribePlanEdits,
  subscribePlanReviews as repoSubscribePlanReviews,
  subscribePlanRun as repoSubscribePlanRun,
  subscribeSiteInventory as repoSubscribeSiteInventory,
  subscribeTopics as repoSubscribeTopics,
} from '@/repositories/plan'
import { useBrandStore } from '@/stores/brand'
import { useKeyedSubscription } from '@/stores/keyed-subscription'
import {
  type ScopedSubscription,
  currentSetupScope,
  useLatestRun,
  useScopedSubscription,
} from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import { isLoaded } from '@/types/load-state'
import {
  ADD_PAGE_REFUSAL_REASONS,
  PLAN_GATE_REFUSAL_REASONS,
  PLAN_RUN_STALE_AFTER_MS,
  START_PLAN_REFUSAL_REASONS,
  type PlanEditStage,
  type PlanGateReply,
  type PlanRun,
  type PlanRunStage,
  type PlanStepEditsInput,
  type StartPlanInput,
  isPlanStale,
  waitingGate as waitingGateOf,
} from '@/types/plan'
import { runStaleAt as staleAtOf } from '@/types/setup-run'

type DescribeFailure = (failure: CallableError) => string

/** A toast line maker for a plan call: plain words for a known refusal, for a missing topic when the call names one, else the server's message. */
function describeRefusals<R extends string>(
  reasons: readonly R[],
  messages: Readonly<Record<R, string>>,
  namesTopic: boolean,
): DescribeFailure {
  const isRefusal = createReasonGuard(reasons)
  return (failure) => {
    if (isRefusal(failure.reason)) return i18n.global.t(messages[failure.reason])
    if (namesTopic && failure.code === 'functions/not-found') {
      return i18n.global.t(TOPIC_MISSING_MESSAGE)
    }
    return failure.message
  }
}

const describeGateFailure = describeRefusals(
  PLAN_GATE_REFUSAL_REASONS,
  PLAN_GATE_REFUSAL_MESSAGES,
  false,
)
const describeAddPageFailure = describeRefusals(
  ADD_PAGE_REFUSAL_REASONS,
  ADD_PAGE_REFUSAL_MESSAGES,
  true,
)

/** Runs a plan call with `pending` raised; `false` on failure (toasted with `describe`'s line). */
async function callPlan(
  pending: Ref<boolean>,
  call: () => Promise<unknown>,
  describe: DescribeFailure = describeGateFailure,
): Promise<boolean> {
  pending.value = true
  try {
    await call()
    return true
  } catch (cause) {
    useToastStore().error(
      i18n.global.t('errors.somethingWentWrong'),
      describe(parseCallableError(cause)),
    )
    return false
  } finally {
    pending.value = false
  }
}

/** The page-first plan of the current brand: the latest run, the site inventory, the planned pages and their keywords' ranks. */
export const usePlanStore = defineStore('plan', () => {
  const runSubscription = useLatestRun<PlanRun>(repoSubscribeLatestPlanRun, PLAN_RUN_STALE_AFTER_MS)
  const runByIdSubscription = useKeyedSubscription((scope, runId) =>
    repoSubscribePlanRun(scope, runId),
  )
  const reviewsSubscription = useKeyedSubscription((scope, runId) =>
    repoSubscribePlanReviews(scope, runId),
  )
  const editsSubscription = useKeyedSubscription((scope, runId) =>
    repoSubscribePlanEdits(scope, runId),
  )
  const completedRunSubscription = useScopedSubscription(repoSubscribeLatestCompletedPlanRun)
  const inventorySubscription = useScopedSubscription(repoSubscribeSiteInventory)
  const inventoryPagesSubscription = useScopedSubscription(repoSubscribeInventoryPages)
  const topicsSubscription = useScopedSubscription(repoSubscribeTopics)
  const pagesSubscription = useScopedSubscription(repoSubscribePages)
  const pageKeywordsSubscription = useScopedSubscription(repoSubscribePageKeywords)
  const keywordRanksSubscription = useScopedSubscription(repoSubscribeKeywordRanks)

  function retainerOf(
    subscriptions: readonly Pick<ScopedSubscription<unknown>, 'open' | 'close' | 'fail'>[],
  ): () => () => void {
    const retainer = createRetainer({
      open() {
        const scope = currentSetupScope()
        for (const subscription of subscriptions) {
          if (scope) subscription.open(scope)
          else subscription.fail(i18n.global.t('common.noWorkspaceSelected'))
        }
      },
      close() {
        for (const subscription of subscriptions) subscription.close()
      },
    })
    onScopeDispose(retainer.dispose)
    return retainer.retain
  }

  const starting = ref(false)
  const cancelling = ref(false)
  const answering = ref(false)
  const rerunning = ref(false)
  const editing = ref(false)
  const discarding = ref(false)
  const addingPage = ref(false)

  const latestRun = runSubscription.state
  const run = runSubscription.run
  const isRunLive = runSubscription.isLive
  const waitingGate = computed(() => (run.value ? waitingGateOf(run.value) : null))
  const runStaleAt = computed(() =>
    run.value ? staleAtOf(run.value, PLAN_RUN_STALE_AFTER_MS) : null,
  )

  const planStale = computed(() => {
    const completed = completedRunSubscription.state.value
    const strategy = useBrandStore().contentStrategy
    return (
      !isRunLive.value &&
      isLoaded(completed) &&
      isLoaded(strategy) &&
      isPlanStale(completed.value, strategy.value.meta.confirmedAt)
    )
  })

  const runRetainer = createRetainer({
    open() {
      const scope = currentSetupScope()
      for (const subscription of [runSubscription, completedRunSubscription]) {
        if (scope) subscription.open(scope)
        else subscription.fail(i18n.global.t('common.noWorkspaceSelected'))
      }
    },
    close() {
      runSubscription.close()
      completedRunSubscription.close()
    },
  })
  onScopeDispose(runRetainer.dispose)

  const inventory = inventorySubscription.state
  const inventoryPages = inventoryPagesSubscription.state
  const topics = topicsSubscription.state
  const pages = pagesSubscription.state
  const pageKeywords = pageKeywordsSubscription.state
  const keywordRanks = keywordRanksSubscription.state

  /** Opens a plan run, or one that finds more pages for `input.focusTopicId`, with an optional steer note and market; `false` on failure (toasted). */
  function startPlan(input: StartPlanInput = {}): Promise<boolean> {
    return callPlan(
      starting,
      () => repoStartPlanRun(input),
      describeRefusals(
        START_PLAN_REFUSAL_REASONS,
        START_PLAN_REFUSAL_MESSAGES,
        input.focusTopicId !== undefined,
      ),
    )
  }

  /** Cancels the live plan run; `false` when none is live or the call fails (toasted). */
  async function cancelPlan(): Promise<boolean> {
    const live = run.value
    if (!live || !isRunLive.value) return false
    return callPlan(cancelling, () => repoCancelPlanRun(live.id))
  }

  /** Answers the gate the latest run waits at; `false` when it waits at another gate or at none, or the call fails (toasted). */
  async function answerGate(reply: PlanGateReply): Promise<boolean> {
    const waiting = run.value
    if (!waiting || waitingGate.value !== reply.stage) return false
    return callPlan(answering, () => repoAnswerPlanGate({ runId: waiting.id, ...reply }))
  }

  /** Opens a new run from `stage` of the latest run, keeping the stages before it; `false` when there is no run or the call fails (toasted). */
  async function rerunFrom(stage: PlanRunStage): Promise<boolean> {
    const source = run.value
    if (!source) return false
    return callPlan(rerunning, () => repoRerunPlanFrom({ runId: source.id, stage }))
  }

  /** Saves what the person changed in one stage of the latest run; `false` when there is no run or the call fails (toasted). */
  async function editStep(stage: PlanEditStage, edits: PlanStepEditsInput): Promise<boolean> {
    const target = run.value
    if (!target) return false
    return callPlan(editing, () => repoEditPlanStep({ runId: target.id, stage, edits }))
  }

  /** Discards the run that waits at a gate; `false` when none waits or the call fails (toasted). */
  async function discardProposal(): Promise<boolean> {
    const waiting = run.value
    if (!waiting || !waitingGate.value) return false
    return callPlan(discarding, () => repoDiscardPlanProposal(waiting.id))
  }

  /** Adds one page by hand to a topic; resolves the new page's id, or `null` while another add is in flight or when the call fails (toasted). */
  async function addPage(
    topicId: string,
    question: string,
    keyword?: string,
  ): Promise<string | null> {
    if (addingPage.value) return null
    let pageId: string | null = null
    await callPlan(
      addingPage,
      async () => {
        pageId = (await repoAddPage({ topicId, question, keyword })).pageId
      },
      describeAddPageFailure,
    )
    return pageId
  }

  return {
    latestRun,
    runById: runByIdSubscription.state,
    run,
    isRunLive,
    runStaleAt,
    planStale,
    waitingGate,
    reviews: reviewsSubscription.state,
    edits: editsSubscription.state,
    inventory,
    inventoryPages,
    topics,
    pages,
    pageKeywords,
    keywordRanks,
    starting,
    cancelling,
    answering,
    rerunning,
    editing,
    discarding,
    addingPage,
    retainRun: runRetainer.retain,
    retainRunById: runByIdSubscription.retain,
    retainReviews: reviewsSubscription.retain,
    retainEdits: editsSubscription.retain,
    retainInventory: retainerOf([inventorySubscription, inventoryPagesSubscription]),
    retainPlan: retainerOf([topicsSubscription, pagesSubscription, pageKeywordsSubscription]),
    retainRanks: retainerOf([keywordRanksSubscription]),
    startPlan,
    cancelPlan,
    answerGate,
    rerunFrom,
    editStep,
    discardProposal,
    addPage,
  }
})
